/**
 * LoginPage — valida credencial contra /api/me ANTES de autenticar.
 *
 * Fluxo:
 * 1. btoa(user:pass) + GET /api/me com Authorization: Basic <token>.
 * 2. 200  → persiste sessionStorage + onAuthenticated() (seta authed=true).
 * 3. 401  → clearAuth() + mostra "Usuário ou senha inválidos".
 * 4. outro erro / rede → clearAuth() + mostra "Erro de rede...".
 *
 * CRÍTICO: setAuthed(true) só pode ser chamado no branch do 200. Qualquer
 * outro path limpa sessionStorage (evita cred inválida ficar pendurada e
 * vazar em reload futuro quando o useAuth inicial for chamado).
 */
import { useState } from "react";
import { AuthRequiredError, clearAuth, getMe, setAuth } from "../api/client";
import logoIndra from "../assets/logos/logo-indra-white-color.svg";
import logoCortex from "../assets/logos/cortex/logo.svg";

interface Props {
  onAuthenticated: () => void;
}

export function LoginPage({ onAuthenticated }: Props) {
  const [user, setUser] = useState("");
  const [pass, setPass] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      // #246: valida via client tipado (getMe) com a credencial digitada SEM
      // persistir. O override de token mantém o invariante validate-then-persist:
      // setAuth só roda depois do 200.
      const token = btoa(`${user}:${pass}`);
      await getMe(token);
      // Só agora — depois do 200 — persiste e autentica.
      setAuth(user, pass);
      onAuthenticated();
    } catch (err) {
      clearAuth();
      if (err instanceof AuthRequiredError) {
        setError("Usuário ou senha inválidos"); // 401
      } else if (err instanceof Error && err.message.startsWith("HTTP ")) {
        setError("Falha ao validar. Tente novamente."); // outro não-ok
      } else {
        setError("Erro de rede. Verifique sua conexão."); // rede/fetch
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indra-azul to-indra-azul-deep p-6">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-[400px] bg-indra-azul-surface rounded-xl shadow-2xl p-8 border border-white/5"
      >
        <div className="flex flex-col items-center mb-6">
          <div className="flex items-center gap-5 mb-5">
            <img src={logoIndra} alt="Indra Energia" className="h-10" />
            <div className="w-px h-10 bg-white/15" />
            <img src={logoCortex} alt="Cortex" className="h-10" />
          </div>
          <p className="text-white/60 text-sm mt-1 text-center">
            Inteligência financeira Indra Energia
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-white/60 mb-1.5">
              Usuário
            </label>
            <input
              autoFocus
              type="text"
              value={user}
              onChange={(e) => setUser(e.target.value)}
              placeholder="seu.usuario"
              className="w-full px-3 py-2.5 bg-indra-azul border border-indra-turquesa/20 rounded-md text-white placeholder-white/30 text-sm focus:outline-none focus:border-indra-turquesa transition-colors"
            />
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider text-white/60 mb-1.5">
              Senha
            </label>
            <input
              type="password"
              value={pass}
              onChange={(e) => setPass(e.target.value)}
              placeholder="••••••••"
              className="w-full px-3 py-2.5 bg-indra-azul border border-indra-turquesa/20 rounded-md text-white placeholder-white/30 text-sm focus:outline-none focus:border-indra-turquesa transition-colors"
            />
          </div>

          <button
            type="submit"
            disabled={!user || !pass || submitting}
            className="w-full bg-indra-turquesa text-indra-azul font-bold py-2.5 rounded-md text-sm uppercase tracking-wider hover:bg-indra-turquesa/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? "Validando..." : "Entrar"}
          </button>

          {error && (
            <p role="alert" className="text-red-400 text-sm text-center">
              {error}
            </p>
          )}
        </div>

        <p className="text-center text-[11px] text-white/40 mt-6">
          Acesso restrito aos sócios
        </p>
      </form>
    </div>
  );
}
